import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge, StatCard } from "../components/shared";
import { formatINR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Landmark, Plus, ArrowLeftRight } from "lucide-react";

export default function Accounts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { open } = useActions();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/accounts"); setItems(data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  const totalCash = items.filter((a) => ["Cash", "Petty Cash"].includes(a.type)).reduce((s, a) => s + a.balance, 0);
  const totalBank = items.filter((a) => !["Cash", "Petty Cash"].includes(a.type)).reduce((s, a) => s + a.balance, 0);

  return (
    <div>
      <PageHeader title="Accounts" subtitle="Cash, bank & UPI accounts with live balances"
        actions={<div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => open("transfer")} data-testid="transfer-btn" className="gap-1.5"><ArrowLeftRight className="h-4 w-4" /> Transfer</Button>
          <Button size="sm" onClick={() => open("add_account")} data-testid="add-account-btn" className="bg-[#0F2942] hover:bg-[#163a5c] gap-1.5"><Plus className="h-4 w-4" /> Add Account</Button>
        </div>} />
      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <StatCard label="Total Cash" value={totalCash} accent="emerald" />
            <StatCard label="Total Bank" value={totalBank} accent="blue" />
            <StatCard label="Grand Total" value={totalCash + totalBank} accent="teal" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <div key={a.id} data-testid={`account-card-${a.id}`} onClick={() => navigate(`/accounts/${a.id}`)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm hover:shadow-md cursor-pointer transition-all">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-lg bg-[#0F2942]/5 dark:bg-teal-500/10 flex items-center justify-center"><Landmark className="h-4 w-4 text-[#0F2942] dark:text-teal-400" /></div>
                    <div><div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{a.name}</div>
                      <Badge cls="bg-slate-100 text-slate-600 border-slate-200">{a.type}</Badge></div>
                  </div>
                  <span className={`text-xs ${a.status === "active" ? "text-emerald-600" : "text-slate-400"}`}>{a.status}</span>
                </div>
                <div className="font-mono text-2xl font-bold text-slate-900 dark:text-slate-50 mt-3">{formatINR(a.balance)}</div>
                <div className="text-[11px] text-slate-400 mt-1">Opening {formatINR(a.opening_balance)}{a.bank_name ? ` · ${a.bank_name}` : ""}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
